import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { legacy } from '@/shared/api/client';
import { PLATFORM_CONFIG } from '@/mocks/platform';
import type { PlatformConfig, PlatformSender, SystemEmail, SystemEmailKind, SystemPush, SystemPushKind } from './types';

interface PlatformContextValue {
  config: PlatformConfig;
  saveEmail: (kind: SystemEmailKind, patch: Partial<SystemEmail>) => void;
  savePush: (kind: SystemPushKind, patch: Partial<SystemPush>) => void;
  saveSender: (sender: PlatformSender) => void;
  /** `POST /v1/platform/emails/:kind/test` — manda o e-mail do sistema para um endereço, com os exemplos. */
  sendTestEmail: (kind: SystemEmailKind, to: string) => Promise<void>;
}

const PlatformContext = createContext<PlatformContextValue | null>(null);

/** Memória da sessão até a API de plataforma existir (docs/plataforma-integracao.md). */
export function PlatformProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PlatformConfig>(() => structuredClone(PLATFORM_CONFIG));

  const saveEmail = useCallback((kind: SystemEmailKind, patch: Partial<SystemEmail>) => {
    setConfig((prev) => ({ ...prev, emails: prev.emails.map((item) => (item.kind === kind ? { ...item, ...patch } : item)) }));
  }, []);
  const savePush = useCallback((kind: SystemPushKind, patch: Partial<SystemPush>) => {
    setConfig((prev) => ({ ...prev, pushes: prev.pushes.map((item) => (item.kind === kind ? { ...item, ...patch } : item)) }));
  }, []);
  const saveSender = useCallback((sender: PlatformSender) => {
    setConfig((prev) => ({ ...prev, sender }));
  }, []);

  const sendTestEmail = useCallback(async (kind: SystemEmailKind, to: string) => {
    await legacy.post(`/platform/emails/${encodeURIComponent(kind)}/test`, { to });
  }, []);

  const value = useMemo(
    () => ({ config, saveEmail, savePush, saveSender, sendTestEmail }),
    [config, saveEmail, savePush, saveSender, sendTestEmail],
  );
  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePlatform(): PlatformContextValue {
  const ctx = useContext(PlatformContext);
  if (!ctx) throw new Error('usePlatform deve ficar dentro de /plataforma.');
  return ctx;
}
